import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaPrint,
  FaTimes,
  FaDownload,
  FaBoxOpen,
  FaEye,
  FaFilter
} from "react-icons/fa";

import {
  ArrowDownToLine,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const API_BASE =
  "https://nissi-india.com/api";

const STNInwardReport = () => {
  const {
    isDark,
    skin,
    setSkin,
  } =
    useContext(
      ThemeContext
    );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showForm,
    setShowForm,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTERS
  ========================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    stnInwardNo,
    setStnInwardNo,
  ] = useState("");

  const [
    status,
    setStatus,
  ] = useState("");

  const [
    inwardType,
    setInwardType,
  ] = useState("");

  const [
    requestedStore,
    setRequestedStore,
  ] = useState("");

  /* =========================================================
     DROPDOWN / DATA
  ========================================================= */

  const [
    storeList,
    setStoreList,
  ] = useState([]);

  const [
    results,
    setResults,
  ] = useState([]);

  /* =========================================================
     LOCAL STORAGE VALUES
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const branchType =
    (
      localStorage.getItem(
        "branchType"
      ) || "HO"
    ).toUpperCase();

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  const loginUserId =
    localStorage.getItem(
      "loginUserId"
    ) ||
    userId ||
    "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branchType,

        "x-userid":
          userId,

        "x-loginuserid":
          loginUserId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branchType,
        userId,
        loginUserId,
      ]
    );

  /* =========================================================
     COMMON INPUT DESIGN
     SAME AS DEPARTMENT MASTER
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     DATE FORMATTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatDateForApi =
    (dateStr) => {
      if (!dateStr) {
        return "";
      }

      const [
        year,
        month,
        day,
      ] =
        dateStr.split(
          "-"
        );

      return `${day}/${month}/${year}`;
    };

  const formatDate =
    (dateStr) => {
      if (!dateStr) {
        return "-";
      }

      const date =
        new Date(
          dateStr
        );

      return Number.isNaN(
        date.getTime()
      )
        ? dateStr
        : date.toLocaleDateString(
            "en-GB"
          );
    };

  /* =========================================================
     CURRENCY FORMAT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatCurrency =
    (amount) => {
      return new Intl.NumberFormat(
        "en-IN",
        {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(
        Number(
          amount
        ) || 0
      );
    };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setFromDate("");

      setToDate("");

      setStnInwardNo("");

      setStatus("");

      setInwardType("");

      setRequestedStore("");

      setResults([]);
    };

  /* =========================================================
     STATUS COLOR
     CORE LOGIC UNCHANGED
  ========================================================= */

  const getStatusColor =
    (statusText) => {
      switch (
        (
          statusText ||
          ""
        )
          .trim()
          .toLowerCase()
      ) {
        case "waiting for outward":
          return isDark
            ? "bg-yellow-900/30 text-yellow-300"
            : "bg-yellow-100 text-yellow-700";

        case "waiting for inward":
          return isDark
            ? "bg-blue-900/30 text-blue-300"
            : "bg-blue-100 text-blue-700";

        case "inwarded":
          return isDark
            ? "bg-green-900/30 text-green-300"
            : "bg-green-100 text-green-700";

        case "disapproved":
        case "disapproved ":
          return isDark
            ? "bg-red-900/30 text-red-300"
            : "bg-red-100 text-red-700";

        case "closed":
          return isDark
            ? "bg-gray-700 text-gray-200"
            : "bg-gray-200 text-gray-700";

        case "canceled":
        case "cancelled":
          return isDark
            ? "bg-red-900/30 text-red-300"
            : "bg-red-100 text-red-700";

        default:
          return isDark
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-100 text-gray-700";
      }
    };

  /* =========================================================
     FETCH STORES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchStores =
    async () => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/fillStoreDropdown_rpt`,
            {
              params: {
                session_branchid:
                  branchId,
              },

              headers:
                commonHeaders,
            }
          );

        const stores =
          Array.isArray(
            res.data
              ?.data
          )
            ? res.data
                .data
            : Array.isArray(
                res.data
              )
            ? res.data
            : [];

        setStoreList(
          stores
        );
      } catch (
        error
      ) {
        console.error(
          "Store dropdown fetch failed",
          error
        );

        toast.error(
          "Failed to load outward store dropdown"
        );
      }
    };

  useEffect(() => {
    fetchStores();
  }, [
    branchId,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    async () => {
      if (
        !fromDate ||
        !toDate
      ) {
        toast.warning(
          "Please select both From Date and To Date"
        );

        return;
      }

      try {
        setLoading(
          true
        );

        const params = {
          branchID:
            branchId ||
            "8",

          inwardType:
            inwardType ||
            "DO",

          stnReqNo:
            stnInwardNo ||
            "%",

          fromDate:
            formatDateForApi(
              fromDate
            ),

          toDate:
            formatDateForApi(
              toDate
            ),

          requestedStore:
            requestedStore ||
            "Select",

          status:
            status ||
            "Select",
        };

        const res =
          await axios.get(
            `${API_BASE}/getStnInwardReportList_rpt`,
            {
              params,

              headers:
                commonHeaders,
            }
          );

        const list =
          Array.isArray(
            res.data
              ?.data
          )
            ? res.data
                .data
            : Array.isArray(
                res.data
              )
            ? res.data
            : [];

        setResults(
          list
        );

        toast.success(
          `Loaded ${list.length} record(s)`
        );
      } catch (
        error
      ) {
        console.error(
          "STN Inward fetch failed",
          error
        );

        toast.error(
          "Failed to fetch STN Inward list"
        );

        setResults([]);
      } finally {
        setLoading(
          false
        );
      }
    };

  /* =========================================================
     EXPORT
     ORIGINAL CSV FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExport =
    () => {
      if (
        !results.length
      ) {
        toast.warn(
          "No data available to export"
        );

        return;
      }

      const headers = [
        "Req No",
        "Inward No",
        "Date",
        "From Branch",
        "Amount",
        "Status",
      ];

      const rows =
        results.map(
          (item) => [
            item
              .fld_STNRequestNo ||
              "",

            item
              .InwardNo ||
              "",

            item
              .InwardDate ||
              "",

            item
              .FromB ||
              "",

            item
              .Amount ||
              0,

            item
              .isoutwardclosed ||
              "",
          ]
        );

      const csvContent =
        [
          headers,
          ...rows,
        ]
          .map(
            (row) =>
              row
                .map(
                  (col) =>
                    `"${String(
                      col
                    ).replace(
                      /"/g,
                      '""'
                    )}"`
                )
                .join(
                  ","
                )
          )
          .join(
            "\n"
          );

      const blob =
        new Blob(
          [
            csvContent,
          ],
          {
            type: "text/csv;charset=utf-8;",
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href =
        url;

      link.setAttribute(
        "download",
        "stn_inward_report.csv"
      );

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );
    };

  /* =========================================================
     PRINT ALL
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrintReport =
    () => {
      const params = {
        branchID:
          branchId ||
          "8",

        inwardType:
          inwardType ||
          "DO",

        stnReqNo:
          stnInwardNo ||
          "%",

        fromDate:
          fromDate
            ? formatDateForApi(
                fromDate
              )
            : "",

        toDate:
          toDate
            ? formatDateForApi(
                toDate
              )
            : "",

        requestedStore:
          requestedStore ||
          "Select",

        status:
          status ||
          "Select",
      };

      const query =
        new URLSearchParams(
          params
        ).toString();

      window.open(
        `/inventory/STNInwardReport_AllPrint?${query}`,
        "_blank"
      );
    };

  /* =========================================================
     VIEW / PRINT ROW
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewRow =
    (row) => {
      sessionStorage.setItem(
        "printData",
        JSON.stringify(
          row
        )
      );

      window.open(
        `/inventory/STNInwardReport_Print?id=${row.fld_STN_Inwardid}`,
        "_blank"
      );
    };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            STN Inward Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FORM CARD
            SAME DESIGN STRUCTURE AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              FORM HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT SIDE */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ArrowDownToLine className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  STN Inward Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Search and review stock transfer inward records
                </p>
              </div>
            </div>

            {/* =============================================
                RIGHT HEADER ACTIONS
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT
                  SAME THEME AS SEARCH */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={
                    14
                  }
                />

                Export
              </button>

              {/* SHOW/HIDE */}

              <button
  type="button"
  onClick={() => setShowForm(!showForm)}
  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`}
>
  <FaFilter size={14} />

  {showForm ? "Hide Filters" : "Show Filters"}
</button>
            </div>
          </div>

          {/* ===============================================
              FORM BODY
          =============================================== */}

          {showForm && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      e
                    ) =>
                      setFromDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      e
                    ) =>
                      setToDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* INWARD NO */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    STN Inward No
                  </label>

                  <input
                    type="text"
                    value={
                      stnInwardNo
                    }
                    onChange={(
                      e
                    ) =>
                      setStnInwardNo(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter STN inward number"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* STATUS */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Status
                  </label>

                  <select
                    value={
                      status
                    }
                    onChange={(
                      e
                    ) =>
                      setStatus(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Status
                    </option>

                    <option value="Waiting For Outward">
                      Waiting For Outward
                    </option>

                    <option value="Waiting For Inward">
                      Waiting For Inward
                    </option>

                    <option value="Inwarded">
                      Inwarded
                    </option>

                    <option value="DisApproved">
                      DisApproved
                    </option>

                    <option value="Closed">
                      Closed
                    </option>

                    <option value="Canceled">
                      Canceled
                    </option>
                  </select>
                </div>

                {/* INWARD TYPE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Inward Type
                  </label>

                  <select
                    value={
                      inwardType
                    }
                    onChange={(
                      e
                    ) =>
                      setInwardType(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Types
                    </option>

                    <option value="From Outward">
                      From Outward
                    </option>

                    <option value="From Direct Outward">
                      From Direct Outward
                    </option>

                    <option value="From IN/Out (Rejected QTY)">
                      From IN/Out (Rejected QTY)
                    </option>
                  </select>
                </div>

                {/* OUTWARD STORE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Outward Store
                  </label>

                  <select
                    value={
                      requestedStore
                    }
                    onChange={(
                      e
                    ) =>
                      setRequestedStore(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stores
                    </option>

                    {storeList.map(
                      (
                        store,
                        index
                      ) => (
                        <option
                          key={
                            store.fld_storeid ||
                            store.value ||
                            index
                          }
                          value={
                            store.fld_storeid ||
                            store.value
                          }
                        >
                          {store.fld_storename ||
                            store.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  FORM ACTION BUTTONS
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={
                      14
                    }
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrintReport
                  }
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    rounded-xl
                    transition
                    ${
                      isDark
                        ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }
                  `}
                >
                  <FaPrint
                    size={
                      14
                    }
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch
                      size={
                        14
                      }
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME STRUCTURE AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >
            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  STN Inward Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {
                    results.length
                  }{" "}
                  records
                </span>
              </div>
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                py-16
              "
            >
              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading STN inward records...
              </p>
            </div>
          ) : results.length > 0 ? (

            /* =============================================
               RESULTS TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* TABLE HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Sl No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Req No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Inward No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      From Branch
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Amount
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      View
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Print
                    </th>
                  </tr>
                </thead>

                {/* TABLE BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {results.map(
                    (
                      row,
                      idx
                    ) => (
                      <tr
                        key={
                          row.fld_STN_Inwardid ||
                          idx
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SL NO */}

                        <td className="px-4 py-3 font-medium">
                          {
                            idx +
                            1
                          }
                        </td>

                        {/* REQUEST NO */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {row.fld_STNRequestNo ||
                              "-"}
                          </span>
                        </td>

                        {/* INWARD NO */}

                        <td className="px-4 py-3">

                          <span
                            className={`
                              inline-block
                              px-2.5
                              py-1
                              rounded-full
                              text-xs
                              font-semibold
                              ${
                                isDark
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-700"
                              }
                            `}
                          >
                            {row.InwardNo ||
                              "-"}
                          </span>
                        </td>

                        {/* DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">

                          {row.InwardDate ||
                            formatDate(
                              row.fld_date
                            )}
                        </td>

                        {/* FROM BRANCH */}

                        <td className="px-4 py-3 font-medium">

                          {row.FromB ||
                            "-"}
                        </td>

                        {/* AMOUNT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatCurrency(
                            row.Amount
                          )}
                        </td>

                        {/* STATUS */}

                        <td className="px-4 py-3 whitespace-nowrap">

                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              row.isoutwardclosed
                            )}`}
                          >
                            {row.isoutwardclosed ||
                              "Not Closed"}
                          </span>
                        </td>

                        {/* VIEW */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handleViewRow(
                                row
                              )
                            }
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>

                        {/* PRINT */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handleViewRow(
                                row
                              )
                            }
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                            title="Print"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBoxOpen
                size={
                  40
                }
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <p
                className={`text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                No STN inward records found. Please use the filters above to search.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={
          3000
        }
        hideProgressBar={
          false
        }
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default STNInwardReport;